import { Row, Spin, Steps } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { message } from "@bindeleddet/core/utils";
import { Link } from "react-router-dom";
import TitleUnderlined from "components/TitleUnderlined/TitleUnderlined";
import { useViewport } from "@bindeleddet/core/hooks";
import { isCompanyUser, useLoginContext } from "@bindeleddet/core/contexts/login";
import { useCallback, useEffect, useRef, useState } from "react";
import Booking, { DATE_TAKEN_MESSAGE } from "./Booking/Booking";
import BookingClosed from "./BookingClosed/BookingClosed";
import BookingError from "./BookingError/BookingError";
import {
  BookingProblem,
  CONTACT_PROBLEMS,
  bookingErrorCode,
  bookingErrorMessage,
  getBookedDays,
  getBookingProblems,
  placeBookingRequest,
} from "./bookingRequest";
import Countdown from "./Countdown/Countdown";
import { NO_AVAILABLE_SEASON, UNKNOWN_ERROR } from "./interface";
import NoSeason from "./NoSeason/NoSeason";
import TermsAndContact from "./Information/TermsAndContact";
import Summary from "./Summary/Summary";
import useCurrentBookingSeason, { isBookingSeason } from "./useCurrentBookingSeason";

const EventBooking = () => {
  const [current, setCurrent] = useState(0);
  const [date, setDate] = useState<Date | undefined>();
  const [description, setDescription] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const season = useCurrentBookingSeason();
  const [now, setNow] = useState(() => new Date());
  // How far this device's clock is behind the server's (ms). Applied so that a wrong clock on the company's computer
  // doesn't make the countdown end too early or too late.
  const [clockOffset, setClockOffset] = useState(0);
  const serverNow = now.getTime() + clockOffset;

  const openTime = isBookingSeason(season) ? new Date(season.booking_open) : undefined;
  const openTimestamp = openTime?.getTime();
  const secondsLeft = openTimestamp ? Math.ceil((openTimestamp - serverNow) / 1000) : 0;
  const notOpenYet = isBookingSeason(season) && secondsLeft > 0;
  const canBook = isBookingSeason(season) && !notOpenYet;

  // Asks the backend what (if anything) prevents the company from booking. Before opening this is used to show
  // company problems on the countdown; after opening it decides between the calendar and the error page.
  const hasSeason = isBookingSeason(season);
  const [eligibility, setEligibility] = useState<{
    problems?: BookingProblem[];
    error?: string;
    checkedWhenOpen: boolean;
  }>({ checkedWhenOpen: false });
  const [checks, setChecks] = useState(0);

  // Whether this page showed the countdown, i.e. the user was waiting when booking opened
  const sawCountdown = useRef(false);
  if (notOpenYet) sawCountdown.current = true;
  const firstOpenCheckDone = useRef(false);

  useEffect(() => {
    if (!hasSeason) return;
    let cancelled = false;
    const checkedWhenOpen = canBook;
    // Everyone waiting on the countdown reaches 0 in the same second. A random delay of up to 1.5 s before the first
    // check after opening spreads those requests out.
    const spreadOut = canBook && sawCountdown.current && !firstOpenCheckDone.current;
    if (canBook) firstOpenCheckDone.current = true;
    const check = () => {
      // The previous result stays visible while rechecking, so the page doesn't flicker
      getBookingProblems()
        .then(({ problems, clockOffset: offset }) => {
          if (cancelled) return;
          setEligibility({ problems, checkedWhenOpen });
          // Ignore small changes, which are mostly noise from the request time
          setClockOffset(prev => (Math.abs(prev - offset) > 500 ? offset : prev));
        })
        .catch(e => {
          if (!cancelled) setEligibility({ error: bookingErrorMessage(e), checkedWhenOpen });
        });
    };
    const timeout = setTimeout(check, spreadOut ? Math.random() * 1500 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [hasSeason, canBook, checks]);

  const recheck = useCallback(() => setChecks(c => c + 1), []);

  // All company problems are shown as hints under the countdown
  const companyProblems = eligibility.problems?.filter(problem => problem.code !== "not_open");
  // Missing contact person / e-mail doesn't block the calendar; it is fixed in the "Vilkår og kontakt" step, which
  // can't be completed without it
  const blockingProblems = eligibility.problems?.filter(problem => !CONTACT_PROBLEMS.includes(problem.code));
  const blockingCompanyProblems = blockingProblems?.filter(problem => problem.code !== "not_open");
  const notOpenOnServer = eligibility.problems?.find(problem => problem.code === "not_open");
  // The booking window has passed, but the season is still running; booking now happens by e-mail
  const bookingClosed = eligibility.problems?.some(problem => problem.code === "booking_closed") ?? false;
  const blockedError = eligibility.error ?? (blockingProblems?.length ? blockingProblems[0].message : undefined);

  // When the countdown hits 0, the server may still say booking is closed if the user's clock is slightly ahead.
  // During the first 30 seconds after opening we keep the countdown and recheck until the server gives green light,
  // instead of showing the error page.
  const inGraceWindow = canBook && openTimestamp !== undefined && serverNow - openTimestamp <= 30_000;

  // The clock is only needed for the countdown and the grace window. Once booking is open, ticking every second would
  // needlessly re-render the whole booking flow (calendar, comment field, ...).
  const needsClock = notOpenYet || inGraceWindow;
  useEffect(() => {
    if (!needsClock) return;
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, [needsClock]);
  // Network errors and overloaded-server errors are most likely right at opening, so during the grace window they are
  // retried like "not open yet". The error page is only shown if they persist after the window.
  const waitingForServer =
    inGraceWindow &&
    (!eligibility.checkedWhenOpen ||
      (!blockingCompanyProblems?.length && (notOpenOnServer !== undefined || eligibility.error !== undefined)));

  useEffect(() => {
    if (!waitingForServer || !eligibility.checkedWhenOpen) return;
    // Recheck every 2–3 seconds; the random part spreads the requests from many companies waiting at once
    const timeout = setTimeout(recheck, 2000 + Math.random() * 1000);
    return () => clearTimeout(timeout);
  }, [waitingForServer, eligibility, recheck]);

  const { currentUser } = useLoginContext();
  const { width } = useViewport();
  const breakpoint = 850;

  const goToNextStep = useCallback(() => {
    setCurrent(curr => (curr === 2 ? curr : curr + 1));
  }, []);

  const goToPreviousStep = useCallback(() => {
    setCurrent(curr => (curr === 0 ? curr : curr - 1));
  }, []);

  // Booked days fetched after the season was loaded. Other companies book while the calendar is open, so the season's
  // own list goes stale quickly at opening.
  const [bookedDays, setBookedDays] = useState<string[]>();
  const refreshBookedDays = useCallback(() => {
    getBookedDays()
      .then(setBookedDays)
      .catch(() => {
        // Keep showing the previous list; the dry-run and the booking itself still check availability
      });
  }, []);

  // While the calendar is shown, refresh the booked days: first within 0–2 s (spread out, since everyone arrives at
  // the same time at opening), then every 10–15 s. Skipped while the tab is hidden.
  const calendarShown = canBook && !waitingForServer && !blockedError && current === 0;
  useEffect(() => {
    if (!calendarShown) return;
    let timeout: ReturnType<typeof setTimeout>;
    const schedule = (delay: number) => {
      timeout = setTimeout(() => {
        if (document.visibilityState === "visible") refreshBookedDays();
        schedule(10_000 + Math.random() * 5000);
      }, delay);
    };
    schedule(Math.random() * 2000);
    return () => clearTimeout(timeout);
  }, [calendarShown, refreshBookedDays]);

  const placeBooking = useCallback(() => {
    setLoading(true);
    placeBookingRequest(date, description)
      .then(() => {
        goToNextStep();
      })
      .catch(e => {
        if (bookingErrorCode(e) === "date_taken") {
          // Back to the calendar with fresh booked days; the comment is kept
          setDate(undefined);
          setCurrent(0);
          refreshBookedDays();
          message("warning", `${DATE_TAKEN_MESSAGE} Kommentaren er tatt vare på.`);
          return;
        }
        message("error", bookingErrorMessage(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [date, description, goToNextStep, refreshBookedDays]);

  return (
    <>
      <Row style={width < breakpoint ? { margin: "3% 2.5%" } : { margin: "3% 10%" }}>
        <TitleUnderlined title="Booking av arrangement" />
        {current < 2 && (
          <Row style={{ width: "100%", paddingTop: "10px" }}>
            <Link to="/company/event-booking/">
              <ArrowLeftOutlined /> Tilbake til informasjon om booking og priser
            </Link>
          </Row>
        )}
        {season === undefined || (canBook && !eligibility.checkedWhenOpen && !inGraceWindow) ? (
          <Row justify="center" style={{ width: "100%", padding: "60px 0" }}>
            <Spin size="large" />
          </Row>
        ) : season === NO_AVAILABLE_SEASON ? (
          <NoSeason />
        ) : season === UNKNOWN_ERROR ? (
          <BookingError error="Vi klarte ikke å hente bookingsesongen." onRetry={() => window.location.reload()} />
        ) : bookingClosed && isBookingSeason(season) ? (
          <BookingClosed semester={`${season.is_fall ? "høsten" : "våren"} ${season.year}`} />
        ) : (notOpenYet || waitingForServer) && isBookingSeason(season) && openTime ? (
          <Countdown
            openTime={openTime}
            secondsLeft={Math.max(secondsLeft, 0)}
            waitingForServer={waitingForServer}
            semester={`${season.is_fall ? "høsten" : "våren"} ${season.year}`}
            companyId={isCompanyUser(currentUser) ? currentUser.id : undefined}
            problems={companyProblems}
            onRecheck={recheck}
          />
        ) : blockedError && current === 0 ? (
          <BookingError
            error={blockedError}
            onRetry={recheck}
            problems={blockingCompanyProblems}
            companyId={isCompanyUser(currentUser) ? currentUser.id : undefined}
          />
        ) : (
          <>
            <Steps
              current={current}
              labelPlacement="vertical"
              style={{ padding: "20px 0", width: "100%" }}
              items={[
                {
                  title: "Booking",
                },
                {
                  title: "Vilkår og kontakt",
                },
                {
                  title: "Oppsummering",
                },
              ]}
            />

            {current === 0 ? (
              <Booking
                season={bookedDays ? { ...season, booked_days: bookedDays } : season}
                refreshBookedDays={refreshBookedDays}
                setSelectedDate={setDate}
                setDescription={setDescription}
                goToNextStep={goToNextStep}
                selectedDate={date}
                description={description}
              />
            ) : current === 1 ? (
              <TermsAndContact
                priceTiers={season.price_tiers}
                selectedDate={date}
                loading={loading}
                placeBooking={placeBooking}
                goToPreviousStep={goToPreviousStep}
              />
            ) : (
              <Summary priceTiers={season.price_tiers} bookedDate={date} description={description} />
            )}
          </>
        )}
      </Row>
    </>
  );
};

export default EventBooking;
